import { Delete } from "@mui/icons-material"
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { useEffect } from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import ScenarioFileField from "@features/builder/UpdateMetadata/ScenarioFileField"
import { MetadataFormSectionCard } from "@features/builder/UpdateMetadata/MetadataFormSectionCard"
import { METADATA_FORM_SECTIONS } from "@features/builder/UpdateMetadata/helpers"
import { type MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import { MarkdownEditor } from "@components/MarkdownEditor"

export type MetadataFormScenariosSectionProps = {
  scenarios: string[]
  onDeleteScenarioSource: () => void
}

export const MetadataFormScenariosSection = ({
  scenarios,
  onDeleteScenarioSource,
}: MetadataFormScenariosSectionProps) => {
  const { control, getValues } = useFormContext<MetadataFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: "scenario_descriptions" })

  // list every scenario from the source file, seeding rows for ones not yet saved on this graph
  useEffect(() => {
    scenarios.forEach((scenarioName) => {
      const alreadyExists = getValues("scenario_descriptions")?.some(
        (scenario) => scenario.Name === scenarioName,
      )
      if (alreadyExists) return

      append({ Name: scenarioName, Description: "" })
    })
  }, [scenarios, append, getValues])

  return (
    <MetadataFormSectionCard id={METADATA_FORM_SECTIONS.scenarios} title="Scenarios">
      <Stack direction="column" spacing={2}>
        <ScenarioFileField onDelete={onDeleteScenarioSource} />
        {fields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No scenarios have been saved for this graph.
            <br />
            Your scenarios will appear here once the graph will be saved.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 550 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 220 }}>
                    <strong>Scenario name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                  <TableCell sx={{ width: 60 }}>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => {
                  // scenario descriptions saved on the graph might have been removed from the
                  // scenario file since, keep showing them so the user can still delete them
                  const isOrphaned = !scenarios.includes(field.Name)

                  return (
                    <TableRow
                      key={field.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Stack direction="column">
                          <span>{field.Name}</span>
                          {isOrphaned && (
                            <Typography variant="caption" color="error">
                              No longer in scenario file
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Controller
                          control={control}
                          name={`scenario_descriptions.${index}.Description`}
                          render={({ field: descriptionField, fieldState }) => (
                            <MarkdownEditor
                              {...descriptionField}
                              height={150}
                              errorMessage={fieldState?.error?.message}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        {isOrphaned && (
                          <Tooltip title="Remove scenario description">
                            <IconButton size="small" color="error" onClick={() => remove(index)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </MetadataFormSectionCard>
  )
}

export default MetadataFormScenariosSection
